import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { devAPI } from '@/lib/api';
import { Plus, Trash2, Copy, CheckCircle, X } from 'lucide-react';

const EVENTS = ['user.login', 'user.created', 'user.deleted', 'password.changed', 'mfa.enabled', 'mfa.disabled'];

export default function WebhooksPage() {
  const qc = useQueryClient();
  const { data: hooks = [] } = useQuery({ queryKey: ['dev-webhooks'], queryFn: () => devAPI.getWebhooks() });
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>(['*']);
  const [secret, setSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const create = useMutation({ mutationFn: () => devAPI.createWebhook(url, events), onSuccess: (w) => { setSecret(w.secret); setUrl(''); qc.invalidateQueries({ queryKey: ['dev-webhooks'] }); } });
  const del = useMutation({ mutationFn: (id: string) => devAPI.deleteWebhook(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['dev-webhooks'] }) });
  const toggle = (e: string) => setEvents((p) => (e === '*' ? ['*'] : p.filter((x) => x !== '*').includes(e) ? p.filter((x) => x !== e) : [...p.filter((x) => x !== '*'), e]));

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Webhooks</h1>
      <p className="text-gray-600 mb-6">Receive signed callbacks for your organization's events.</p>

      {secret && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
          <p className="text-sm text-green-800 font-medium mb-1">Signing secret (shown once):</p>
          <div className="flex items-center gap-2">
            <code className="text-xs bg-white px-2 py-1 rounded border break-all flex-1">{secret}</code>
            <button onClick={() => { navigator.clipboard.writeText(secret); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>{copied ? <CheckCircle className="w-4 h-4 text-green-700" /> : <Copy className="w-4 h-4 text-green-700" />}</button>
            <button onClick={() => setSecret(null)}><X className="w-4 h-4 text-gray-400" /></button>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <input placeholder="https://your-app.com/webhooks" value={url} onChange={(e) => setUrl(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" />
        <div className="flex flex-wrap gap-3 mb-3 text-sm">
          <label className="flex items-center gap-1"><input type="checkbox" checked={events.includes('*')} onChange={() => toggle('*')} /> all</label>
          {EVENTS.map((e) => <label key={e} className="flex items-center gap-1"><input type="checkbox" disabled={events.includes('*')} checked={events.includes(e)} onChange={() => toggle(e)} /> {e}</label>)}
        </div>
        <button onClick={() => url && create.mutate()} className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center"><Plus className="w-4 h-4 mr-1" />Add webhook</button>
      </div>

      <div className="space-y-2">
        {hooks.map((w: any) => (
          <div key={w.id} className="bg-white border border-gray-200 rounded-lg p-3 flex items-center justify-between">
            <div><p className="text-sm font-medium break-all">{w.url}</p><p className="text-xs text-gray-500">{(w.events || []).join(', ')}</p></div>
            <button onClick={() => confirm('Delete?') && del.mutate(w.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {hooks.length === 0 && <p className="text-gray-500 text-sm">No webhooks yet.</p>}
      </div>
    </div>
  );
}
